import type { GeoCountry } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { LoadStatus } from "../primitives/load-status";

export type GeoRegionList = GeoCountry["regions"];

/**
 * The country list, cached for the session. Fetched rather than bundled: the
 * backend validates a location against this exact table, so a copy shipped in
 * the SPA could disagree with the validator after a deploy. It changes when the
 * backend is redeployed and at no other time, so it is never refetched.
 */
export class CountriesRepository extends ChangeNotifier {
  #status: LoadStatus = { kind: "idle" };
  #countries: readonly GeoCountry[] = [];

  get status(): LoadStatus {
    return this.#status;
  }

  get countries(): readonly GeoCountry[] {
    return this.#countries;
  }

  /** The regions a country requires, or an empty list where it requires none. */
  regionsFor(code: string): GeoRegionList {
    return this.#countries.find((country) => country.code === code)?.regions ?? [];
  }

  setLoading(): void {
    this.#set({ kind: "loading" });
  }

  setCountries(countries: readonly GeoCountry[]): void {
    this.#countries = [...countries];
    this.#set({ kind: "loaded" });
  }

  setError(message: string): void {
    this.#set({ kind: "error", message });
  }

  #set(status: LoadStatus): void {
    this.#status = status;
    this.notifyListeners();
  }
}
